/**
 * El aspecto de la página pública de una oferta —la de compra, la de reclamar
 * lo gratuito y la de reservar—: lo que se elige en la pestaña «Apariencia» de
 * su ficha, y el CSS que lo aplica.
 *
 * Esas páginas las sirve el worker y no se abren en el editor, así que se
 * pintaban con una paleta fija y la fuente del sistema: el único guiño a la
 * marca era su color de acento. Se veían de Veltica, no del negocio.
 *
 * ── Por oferta ───────────────────────────────────────────────────────────────
 * Cada oferta tiene su página y la configura en su ficha, igual que su título de
 * SEO. Los índices (`/bookings`, `/digital`, `/services`) no son de ninguna
 * oferta —las listan todas— y por eso su aspecto vive en el catálogo.
 *
 * ── Tokens, no valores; vacío es «como hoy» ──────────────────────────────────
 * Se guarda el ID de un token del editor —ver `themeTokenCss`— y el CSS se
 * escribe contra el tema cada vez que se sirve.
 * Cada campo nace vacío y vacío no escribe nada: una oferta publicada no cambia
 * de aspecto porque exista esta pestaña, sólo porque su dueño lo decida.
 *
 * ── Letra automática ─────────────────────────────────────────────────────────
 * Sin letra elegida, la letra sigue al fondo con la regla del editor
 * (`surfaceColor`): blanca sobre oscuro. Con un fondo negro y la letra del sitio
 * —oscura— la página salía negro sobre negro, filos incluidos. La columna de la
 * oferta decide por su cuenta: puede ser oscura sobre una página clara.
 *
 * ── Por qué aquí ─────────────────────────────────────────────────────────────
 * Lo usan el worker, que sirve la página, y el panel, que enseña cómo queda
 * antes de guardar. Una sola función para los dos es lo que garantiza que la
 * vista previa diga la verdad.
 */
import { themeColorVar, tokenRules, tokenStateRules, type ThemeWithTokens } from './themeTokenCss'
import { inkOnColorVar, siteTextColor } from './surfaceColor'
import type { PageTheme } from './types'

/**
 * Qué se enseña del logo del negocio en la columna de la oferta.
 *
 *   - `''`   como hoy: la imagen del logo si la marca la tiene, y si no, nada.
 *   - `text` sólo el NOMBRE de la marca, aunque tenga imagen.
 *   - `both` la imagen y, debajo, el nombre.
 *   - `logo` sólo la imagen.
 *   - `hide` nada: ni imagen ni nombre.
 *
 * Es CSS y no marcado a propósito: las páginas pintan siempre la imagen y el
 * nombre —éste escondido—, y esto decide qué se ve. Así la vista previa del
 * panel lo enseña al momento, sin volver a pedir la página.
 *
 * `show` fue el primer modo —«la imagen, o el nombre si no hay imagen»— y se lee
 * como `both`, que es lo que más se le parece.
 */
export type OfferPageBrandMode = '' | 'text' | 'both' | 'logo' | 'hide'

export const OFFER_PAGE_BRAND_MODES = ['', 'text', 'both', 'logo', 'hide'] as const satisfies readonly OfferPageBrandMode[]

export type OfferPageStyle = {
  /** El logo en la columna. Ver `OfferPageBrandMode`. */
  brand: OfferPageBrandMode
  /** Fondo de la página: id de un color. */
  background: string
  /** Fondo de la columna de la oferta (portada y descripción): id de un color. */
  panel: string
  /** Letra: id de un color. Vacío = automática, según el fondo. */
  text: string
  /**
   * La etiqueta de la columna —«Gratis» o «Reservar»—: id de un color. Vacío =
   * el acento de la marca, como siempre.
   *
   * Existe porque el acento no se elige aquí y no siempre se lee: un verde lima
   * sobre una columna verde clara desaparece. Con un color elegido, la letra es
   * ese color y la pastilla, ese color muy suave — la misma forma de siempre.
   */
  badge: string
  /** Estilos de texto del editor: el nombre de la oferta, el título del trato,
   *  la descripción y los textos de apoyo, y el importe. */
  title: string
  heading: string
  body: string
  price: string
  /** El botón de comprar, reclamar o reservar: id de un estilo de botón. */
  button: string
  /**
   * La barra y el pie DEL SITIO encima y debajo de la página (ver
   * `siteChrome.ts` en el worker). Vacío = se ponen, si el sitio los tiene;
   * `hide` = esta página va sin ellos —un pago sin distracciones—.
   *
   * No son aspecto: no cuentan para `isOfferPageStyleEmpty` ni cargan el tema.
   */
  siteNav: '' | 'hide'
  siteFooter: '' | 'hide'
}

export const OFFER_PAGE_STYLE_FIELDS = [
  'brand', 'background', 'panel', 'text', 'badge', 'title', 'heading', 'body', 'price', 'button',
] as const satisfies readonly (keyof OfferPageStyle)[]

export const EMPTY_OFFER_PAGE_STYLE: OfferPageStyle = {
  brand: '',
  background: '',
  panel: '',
  text: '',
  badge: '',
  title: '',
  heading: '',
  body: '',
  price: '',
  button: '',
  siteNav: '',
  siteFooter: '',
}

/**
 * Con qué nace la página de una oferta NUEVA.
 *
 * Vacío es «como hoy» y así se queda para las que ya existían. Una nueva, en
 * cambio, sale ya vestida con el sitio: el botón principal en vez del apagado
 * de la página, el nombre como un H2, los pasos como un H4, el precio como un H4 y los fondos del
 * tema. Son ids de los tokens que TODO tema trae de fábrica (ver
 * `BUILT_IN_*_COMPONENTS` en `normalize.ts`), así que existen siempre.
 */
export const NEW_OFFER_PAGE_STYLE: OfferPageStyle = {
  ...EMPTY_OFFER_PAGE_STYLE,
  background: 'background',
  panel: 'backgroundSecondary',
  title: 'h2',
  /* «Rellena tus datos», «Elige día y hora»: un H4 y no un H5, que en la
     página de reserva se leía como una nota y no como el paso en el que se
     está. */
  heading: 'h4',
  body: 'p',
  price: 'h4',
  button: 'button-primary',
}

/**
 * Las clases que marcan cada pieza, las mismas en las páginas del worker y en
 * la vista previa del panel. Con clases propias y no con `h1` o `button`: la
 * página de reclamar tiene más de un botón y más de un texto apagado, y el
 * aspecto no puede alcanzar a lo que no se eligió.
 */
export const OFFER_PAGE_CLASS = {
  panel: 'op-panel',
  brandName: 'op-brand-name',
  badge: 'op-badge',
  title: 'op-title',
  heading: 'op-heading',
  text: 'op-text',
  price: 'op-price',
  button: 'op-cta',
} as const

/**
 * Lee lo guardado, sea lo que sea.
 *
 * La columna es `jsonb` libre: puede llegar a medias, con un tipo que no es o
 * con algo que no es un id. Cada campo se comprueba por separado y lo que no
 * vale cae a vacío — o sea, a «como hoy».
 */
export function readOfferPageStyle(raw: unknown): OfferPageStyle {
  const value = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw as Record<string, unknown> : {}
  const out = { ...EMPTY_OFFER_PAGE_STYLE }
  for (const field of OFFER_PAGE_STYLE_FIELDS) {
    if (field === 'brand') continue
    const candidate = value[field]
    out[field] = typeof candidate === 'string' && /^[\w.:-]{1,120}$/.test(candidate) ? candidate : ''
  }
  /* El logo no es un id: sólo vale uno de sus modos. */
  out.brand = value.brand === 'show'
    ? 'both'
    : OFFER_PAGE_BRAND_MODES.includes(value.brand as OfferPageBrandMode) ? value.brand as OfferPageBrandMode : ''
  out.siteNav = value.siteNav === 'hide' ? 'hide' : ''
  out.siteFooter = value.siteFooter === 'hide' ? 'hide' : ''
  return out
}

/** Si no hay nada elegido: entonces el worker ni siquiera carga el tema. */
export function isOfferPageStyleEmpty(style: OfferPageStyle | null | undefined): boolean {
  return !style || OFFER_PAGE_STYLE_FIELDS.every((field) => !style[field])
}

/** Los tokens elegidos que existen en el tema, para saber qué fuentes pedir. */
function chosenTokens(theme: ThemeWithTokens, style: OfferPageStyle) {
  const typography = (id: string) => (id ? theme.typographyComponents?.find((item) => item.id === id) : undefined)
  return {
    title: typography(style.title),
    heading: typography(style.heading),
    body: typography(style.body),
    price: typography(style.price),
    button: style.button ? theme.buttonComponents?.find((item) => item.id === style.button) : undefined,
  }
}

/* Los controles que se seleccionan al reservar no son el CTA, pero forman parte
   de la misma decisión. Exponer la tinta y el relleno del botón como variables
   permite a la página de reservas seguir el botón elegido sin copiar un token
   entero sobre tarjetas que tienen su propia medida y estructura. */
function bookingSelectionVars(styles: {
  background?: string
  backgroundColor?: string
  color?: string
} | undefined): string {
  /* Es el mismo límite que usa `tokenRules`: los temas son datos guardados y no
     pueden abrir una declaración nueva dentro de la hoja pública. */
  const clean = (value: unknown) => typeof value === 'string' ? value.replace(/[<>{};\\]/g, '').trim() : ''
  const fill = clean(styles?.background) || clean(styles?.backgroundColor)
  const ink = clean(styles?.color)
  return [
    fill ? `--booking-selection-fill:${fill}` : '',
    ink ? `--booking-selection-ink:${ink}` : '',
  ].filter(Boolean).join(';')
}

/** Las familias de letra de lo elegido, para `googleFontSpecsFor`. */
export function offerPageStyleFonts(theme: PageTheme | undefined, style: OfferPageStyle | null | undefined): string[] {
  if (!theme || !style || isOfferPageStyleEmpty(style)) return []
  return Object.values(chosenTokens(theme as ThemeWithTokens, style))
    .map((token) => token?.styles?.fontFamily)
    .filter((family): family is string => typeof family === 'string' && family !== '')
}

/**
 * El CSS de lo elegido, para la raíz `root` de la página.
 *
 * Va DESPUÉS de las reglas de la página y gana por orden y por especificidad
 * (la raíz más una clase). Las variables que ya usa la página —`--bg`, `--text`,
 * `--muted`…— se redefinen en la raíz, así que todo lo que no se toca (los
 * campos, los filos, el aviso de error) sigue a la paleta elegida sin
 * reescribirlo.
 *
 * Las variables del tema (`--wb-*`) tiene que declararlas quien llama: el
 * worker en `:root`, el panel en el marco de la vista previa.
 */
export function offerPageStyleCss(theme: PageTheme | undefined, style: OfferPageStyle | null | undefined, root: string): string {
  if (!theme || !style || isOfferPageStyleEmpty(style)) return ''
  const tokens = theme as ThemeWithTokens
  const parts: string[] = []

  const background = themeColorVar(tokens, style.background)
  const panel = themeColorVar(tokens, style.panel)
  const text = themeColorVar(tokens, style.text)
  const declarations: string[] = []
  if (background || panel || text) {
    /*
     * Un color elegido fija la paleta ENTERA, también en modo oscuro.
     *
     * La página cambia a oscuro sola, y un fondo elegido con la letra de ese modo
     * encima —blanco sobre el crema de la marca— no se lee. Así que lo que no se
     * eligió sale del sitio: su fondo, y la columna un punto por encima del
     * fondo, que es la relación que tenía el gris de siempre.
     */
    const bgVar = background ?? '--wb-background'
    /* Sin letra elegida, la del fondo: blanca si es oscuro, como el editor. */
    const autoInk = text ? null : inkOnColorVar(bgVar, tokens)
    /* Un fondo que ES la letra no puede apuntar a `--wb-text` si ésta pasa a
       blanco: se volvería blanco con ella. Entonces va con su valor. */
    const surface = (cssVar: string) => (autoInk && cssVar === '--wb-text' ? siteTextColor(tokens) : `var(${cssVar})`)
    declarations.push(
      `--bg:${surface(bgVar)}`,
      `--text:${autoInk ?? `var(${text ?? '--wb-text'})`}`,
      `--info-bg:${panel ? surface(panel) : 'color-mix(in srgb, var(--text) 3%, var(--bg))'}`,
      '--muted:color-mix(in srgb, var(--text) 62%, var(--bg))',
      '--border:color-mix(in srgb, var(--text) 14%, var(--bg))',
      '--info-border:var(--border)',
      /* El relleno de campos, horas y botones apagados de la página de reservar.
         Las otras dos no lo usan; sin él, sobre un fondo oscuro elegido los
         campos seguían siendo gris claro. */
      '--panel:color-mix(in srgb, var(--text) 6%, var(--bg))',
    )
    /* La letra pasa a ser el `--wb-text`, que es a lo que apuntan los estilos de
       texto del tema. Si lo elegido ES la letra no se toca: sería un ciclo y la
       variable quedaría inválida. */
    if (autoInk) declarations.push(`--wb-text:${autoInk}`)
    else if (text && text !== '--wb-text') declarations.push(`--wb-text:var(${text})`)
    declarations.push('background:var(--bg)', 'color:var(--text)')
    parts.push(`${root}{${declarations.join(';')}}`)

    /* La columna decide su letra por su cuenta: una columna oscura en una página
       clara lleva blanco, y una clara en una oscura vuelve a la letra del sitio
       —como valor, porque `--wb-text` ya es blanco más arriba—. Sus textos
       apagados y sus filos se mezclan con SU fondo, no con el de la página. */
    if (panel && !text) {
      const ink = inkOnColorVar(panel, tokens) ?? siteTextColor(tokens)
      parts.push(`${root} .${OFFER_PAGE_CLASS.panel}{--text:${ink};--wb-text:${ink};--muted:color-mix(in srgb, var(--text) 62%, var(--info-bg));--border:color-mix(in srgb, var(--text) 14%, var(--info-bg));color:var(--text)}`)
    }
  } else {
    /* Sin colores elegidos manda la paleta de la página, clara u oscura. Los
       estilos de texto apuntan a `--wb-text`, la letra del SITIO: sin esto, un
       título elegido saldría con letra oscura sobre el fondo oscuro. */
    parts.push(`${root}{--wb-text:var(--text)}`)
  }

  /* El logo. `!important` al ocultar: el logo claro y el oscuro se muestran y
     se esconden con reglas propias de cada tema, que ganarían a éstas. */
  const brandName = `${root} .${OFFER_PAGE_CLASS.brandName}`
  if (style.brand === 'hide') {
    parts.push(`${root} .logo,${brandName}{display:none !important}`)
  } else if (style.brand === 'logo') {
    parts.push(`${brandName}{display:none !important}`)
  } else if (style.brand === 'text') {
    parts.push(`${root} .logo{display:none !important}`, `${brandName}{display:block}`)
  } else if (style.brand === 'both') {
    /* Con los dos, el nombre va pegado a la imagen: el aire de debajo es el del
       conjunto, no el de cada pieza. */
    parts.push(`${root} .logo{margin-bottom:12px}`, `${brandName}{display:block}`)
  }

  /* La etiqueta «Gratis» o «Reservar»: la letra en el color elegido y la
     pastilla en ese color muy suave. Después de la columna, para que un
     `--wb-text` elegido aquí sea el de la columna.

     La forma de pastilla va AQUÍ y no sólo en la página: en la de reclamar la
     etiqueta ya era una pastilla, pero en la de reservar es un rótulo de
     bloque sin fondo, y ponerle fondo a secas lo pintaba de lado a lado de la
     columna. `justify-self` es por la rejilla que la columna usa en el teléfono
     cuando hay portada, donde un hijo se estira a lo ancho de su celda. */
  const badge = themeColorVar(tokens, style.badge)
  if (badge) {
    parts.push(`${root} .${OFFER_PAGE_CLASS.badge}{display:inline-block;width:auto;justify-self:start;padding:4px 10px;border-radius:999px;background:color-mix(in srgb, var(${badge}) 12%, transparent);color:var(${badge})}`)
  }

  const chosen = chosenTokens(tokens, style)
  parts.push(tokenRules(`${root} .${OFFER_PAGE_CLASS.title}`, chosen.title))
  parts.push(tokenRules(`${root} .${OFFER_PAGE_CLASS.heading}`, chosen.heading))
  parts.push(tokenRules(`${root} .${OFFER_PAGE_CLASS.text}`, chosen.body))
  parts.push(tokenRules(`${root} .${OFFER_PAGE_CLASS.price}`, chosen.price))
  if (chosen.button) {
    const selection = bookingSelectionVars(chosen.button.styles)
    const tabletSelection = bookingSelectionVars({ ...chosen.button.styles, ...chosen.button.breakpoints?.tablet })
    const mobileSelection = bookingSelectionVars({ ...chosen.button.styles, ...chosen.button.breakpoints?.mobile })
    if (selection) parts.push(`${root}{${selection}}`)
    if (tabletSelection && tabletSelection !== selection) parts.push(`@media (max-width: 1024px){${root}{${tabletSelection}}}`)
    if (mobileSelection && mobileSelection !== tabletSelection) parts.push(`@media (max-width: 640px){${root}{${mobileSelection}}}`)

    /* Sin el filo del acento antes del token: la regla de la página lo pone, y
       un botón del editor sin borde lo heredaría. Si el token trae el suyo, va
       después y gana. */
    parts.push(`${root} .${OFFER_PAGE_CLASS.button}{border:0}`)
    parts.push(tokenRules(`${root} .${OFFER_PAGE_CLASS.button}`, chosen.button))
    parts.push(tokenStateRules(`${root} .${OFFER_PAGE_CLASS.button}`, chosen.button))
    /* En reservar, el CTA cierra una cuadrícula de horarios. Un estilo de botón
       de marca puede ser de ancho de contenido y dejarlo flotando bajo la
       primera columna. Conservamos su apariencia, pero la acción ocupa la
       misma anchura y tiene una altura táctil estable. */
    parts.push(`${root} button.submit.${OFFER_PAGE_CLASS.button}{width:100%;min-height:54px;padding:14px 18px;display:flex;align-items:center;justify-content:center;line-height:1.35}`)
  }

  return parts.filter(Boolean).join('\n')
}
